import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Copy, Download, Headset, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminAPI, adminSession, apiError } from '@/lib/adminApi';

const MIN_PASSWORD = 10;

function AuthCard({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-farm-cream flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-9 h-9 rounded-xl bg-farm-primary grid place-items-center">
            <Headset className="w-5 h-5 text-white" />
          </span>
          <p className="font-heading font-bold text-farm-forest">African Farm Support</p>
        </div>
        <h1 className="font-heading text-xl font-bold text-farm-forest mt-4">{title}</h1>
        {subtitle && <p className="text-sm text-farm-forest/70 mt-1">{subtitle}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block mb-3">
      <span className="text-xs font-semibold text-farm-forest/80">{label}</span>
      <Input className="mt-1" {...props} />
    </label>
  );
}

/**
 * Step 2 of signing in: the 6-digit code from an authenticator app.
 * `step` is the password step's answer: { ticket, setup }. With `setup`
 * (first time) it shows the QR code to scan; the first right code then
 * returns one-time recovery codes, shown once before going on.
 */
function AuthenticatorStep({ step, onBack }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const setup = step.setup;

  const finish = (data) => {
    adminSession.save(data);
    navigate('/admin/chats', { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.verifyMfa(step.ticket, code.trim());
      if (res.data.recovery_codes) {
        setSession(res.data);
      } else {
        if (res.data.recovery_codes_left != null) {
          window.alert(`You used a recovery code. ${res.data.recovery_codes_left} left.`);
        }
        finish(res.data);
      }
    } catch (err) {
      if (err.response?.status === 400) onBack();
      setError(apiError(err, 'That code didn\'t work. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (session) {
    const text = session.recovery_codes.join('\n');
    const download = () => {
      const url = URL.createObjectURL(new Blob([`African Farm Support recovery codes\n\n${text}\n`], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'african-farm-support-recovery-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <AuthCard title="Save your recovery codes"
                subtitle="If you lose your phone, each code lets you sign in once. They are shown only now.">
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 rounded-lg p-3 mb-3" data-testid="admin-recovery-codes">
          {session.recovery_codes.map((c) => <li key={c}>{c}</li>)}
        </ul>
        <div className="flex gap-2 mb-4">
          <Button type="button" variant="outline" className="flex-1" onClick={() => navigator.clipboard?.writeText(text)}>
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={download}>
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
        </div>
        <Button className="w-full bg-farm-primary" onClick={() => finish(session)} data-testid="admin-recovery-done">
          I&apos;ve saved them
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={setup ? 'Set up your authenticator' : 'Enter your code'}
      subtitle={setup
        ? 'The support portal needs a code from an authenticator app every time you sign in.'
        : 'Open your authenticator app and enter the 6-digit code for African Farm Support.'}
    >
      {setup && (
        <ol className="text-sm text-farm-forest/80 space-y-2 mb-4 list-decimal pl-5" data-testid="admin-authenticator-setup">
          <li>Install an authenticator app on your phone, such as Google Authenticator or Microsoft Authenticator.</li>
          <li>
            <span>In the app, add an account and scan this code:</span>
            <img src={setup.qr} alt="QR code for your authenticator app" className="w-48 h-48 my-2 mx-auto"
                 data-testid="admin-authenticator-qr" />
            <span className="block text-xs">Can&apos;t scan? Enter this key instead:</span>
            <span className="block font-mono text-xs break-all bg-gray-50 rounded p-2 mt-1" data-testid="admin-authenticator-key">
              {setup.key}
            </span>
          </li>
          <li>Enter the 6-digit code the app shows.</li>
        </ol>
      )}
      <form onSubmit={submit} data-testid="admin-mfa-form">
        <Field label={setup ? '6-digit code' : '6-digit code (or a recovery code)'} value={code}
               onChange={(e) => setCode(e.target.value)} required autoFocus
               inputMode={setup ? 'numeric' : 'text'} autoComplete="one-time-code" maxLength={20}
               data-testid="admin-mfa-code" />
        {error && <p className="text-sm text-red-600 mb-3" role="alert">{error}</p>}
        <Button type="submit" className="w-full bg-farm-primary" disabled={busy} data-testid="admin-mfa-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{setup ? <ShieldCheck className="w-4 h-4 mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />} {setup ? 'Turn on and sign in' : 'Sign in'}</>}
        </Button>
      </form>
      {!setup && (
        <p className="text-xs text-farm-forest/60 mt-3">
          Lost your phone? Enter one of your recovery codes, or ask the portal owner to reset your authenticator.
        </p>
      )}
    </AuthCard>
  );
}

/** /admin when signed out. Portal accounts exist only by invitation. */
export function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(null);

  if (adminSession.token()) return <Navigate to="/admin/chats" replace />;
  if (step) return <AuthenticatorStep step={step} onBack={() => { setStep(null); setPassword(''); }} />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.login(email, password);
      setStep(res.data);
    } catch (err) {
      setError(apiError(err, 'Could not sign in. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Sign in" subtitle="For the African Farm support team. Access is by invitation.">
      <form onSubmit={submit} data-testid="admin-login-form">
        <Field label="Email" type="email" autoComplete="username" value={email}
               onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
        <Field label="Password" type="password" autoComplete="current-password" value={password}
               onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
        {error && <p className="text-sm text-red-600 mb-3" role="alert">{error}</p>}
        <Button type="submit" className="w-full bg-farm-primary" disabled={busy} data-testid="admin-login-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
        </Button>
      </form>
    </AuthCard>
  );
}

const INVITE_KEY = 'farmAfricaAdminInvite';

// The token arrives in the link's #fragment. It is removed from the
// address bar straight away, so it is kept for this tab in
// sessionStorage: the page can be redrawn (or reloaded) without it.
function inviteToken() {
  const match = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash || '');
  if (match) {
    const token = decodeURIComponent(match[1]);
    try { sessionStorage.setItem(INVITE_KEY, token); } catch { /* private mode */ }
    return token;
  }
  try { return sessionStorage.getItem(INVITE_KEY) || ''; } catch { return ''; }
}

/** /admin/invite#token=... — set your name and password to join. */
export function AdminInvitePage() {
  const navigate = useNavigate();
  const [token] = useState(inviteToken);
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState('checking');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(null);

  useEffect(() => {
    // Keep the one-time token out of the address bar and history.
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      setState('invalid');
      return;
    }
    adminAPI.checkInvite(token)
      .then((res) => {
        setInvite(res.data);
        setName(res.data.name || '');
        setState('ready');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.acceptInvite(token, name.trim(), password);
      try { sessionStorage.removeItem(INVITE_KEY); } catch { /* private mode */ }
      setStep(res.data);
    } catch (err) {
      setError(apiError(err, 'Could not set your password. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (step) return <AuthenticatorStep step={step} onBack={() => navigate('/admin', { replace: true })} />;
  if (state === 'checking') {
    return <AuthCard title="Checking your invite…"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></AuthCard>;
  }
  if (state === 'invalid') {
    return (
      <AuthCard title="This invite link doesn't work"
                subtitle="It may have expired or already been used. Ask for a new invite.">
        <Button variant="outline" className="w-full" onClick={() => navigate('/admin')}>Go to sign in</Button>
      </AuthCard>
    );
  }
  return (
    <AuthCard
      title="Join the support team"
      subtitle={`${invite.email}${invite.role === 'superuser' ? ' · portal owner' : ''}. Choose your password.`}
    >
      <form onSubmit={submit} data-testid="admin-invite-form">
        <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80}
               data-testid="admin-invite-name" />
        <Field label={`Password (at least ${MIN_PASSWORD} characters)`} type="password" autoComplete="new-password"
               value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-invite-password" />
        <Field label="Password again" type="password" autoComplete="new-password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} required data-testid="admin-invite-confirm" />
        {error && <p className="text-sm text-red-600 mb-3" role="alert">{error}</p>}
        <Button type="submit" className="w-full bg-farm-primary" disabled={busy} data-testid="admin-invite-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join'}
        </Button>
      </form>
    </AuthCard>
  );
}
